import { Alert, Button, Checkbox, Form, Input, notification, Spin } from "antd";
import React, { useState, useRef } from "react";
import { injectIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "../style.scss";
import { httpService } from "../../../base/services/httpService.service";
import { SessionService } from "../../../base/services/session.service";
import { LocalStorageService } from "../../../base/services/local-storage.service";
import BlockUI from "../../../base/components/BlockUI";
import { scaleImage } from "../../../utils/scaleImage";
import { Constants } from "../../../utils/constants";
import ConfirmationCodeModal from "../register/Confirmation";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { usePersonalDataStore } from "../authStore";
import AuthService, { LogoutReason } from "../../../base/services/authentication.service";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { clearRegistration, openSignUpSession } from "../register/registrationSession";
import { describeOrganizationLock } from "../../../components/Billing/billingCodes";
import { takeBillingEmailLink } from "../../../components/Billing/billingEmailLink";
import {
   getTrialReminderDays,
   hasBillingStatuses,
   isUnfinishedSignUp,
   readOrganizationLock,
   rememberSelectedStatus,
   rememberSignInStatuses
} from "../../../components/Billing/billingSignIn";
import { isEmailNotConfirmed, isInvalidCredentials } from "./loginFailures";

// Every account whose password is right and whose email is confirmed is signed
// in, and each organization comes with a billing status saying what it may do
// (Billing:NewFlow). The routes read that, and hold an organization whose trial
// ended or whose payment failed on the billing page (components/Billing) — for
// whoever may pay for it. A member is turned away here when that organization
// is their only one.
// Failures carry a machine code. The sentences still read are a wrong password
// and an unconfirmed email from a backend not yet redeployed
// (login/loginFailures.js).

// The "add a card before your trial ends" reminder, due on a few days before
// the end of a trial that has no card behind it.
const getLoginTrialReminderDays = (organization) =>
   organization?.billingStatus ? getTrialReminderDays(organization.billingStatus) : null;

// A member whose only organization its owner deactivated is refused with no
// code, only a sentence; the organization comes marked in the refusal's user.
// (Somebody with other organizations signs in and finds it locked in the list.)
const readDeactivatedOrganization = (error) => {
   const envelope = error?.response ? error.response.data : error;
   const organizations = envelope?.data?.user?.organizations;
   if (!Array.isArray(organizations)) return null;
   return organizations.find((x) => x?.isDeletedOrg === true) ?? null;
};

// Said on the form the way a held organization is (describeOrganizationLock).
const describeDeactivatedOrganization = (organizationName) => ({
   title: `${organizationName || "Your organization"} has been deactivated`,
   description: "You'll have access again once its owner reactivates it."
});

function Login() {
   const setPersonalData = usePersonalDataStore((state) => state.setPersonalData);
   const [form] = Form.useForm();
   const nav = useNavigate();
   const [isLogin, setIsLogin] = useState(false);
   const [loginError, setLoginError] = useState(false);
   const [isModaOpen, setIsModaOpen] = useState(false);
   // The address the code was mailed to, so the modal can ask for another one.
   const [unconfirmedEmail, setUnconfirmedEmail] = useState();
   const formRef = useRef(null);
   const { executeRecaptcha } = useGoogleReCaptcha();
   // Signed out by the app because the organization's subscription lapsed
   // while the session was open (base/utils/http.js). The sign in that
   // follows is answered with what the subscription needs.
   const [searchParams] = useSearchParams();
   const isSubscriptionSignOut = searchParams.get("reason") === LogoutReason.SUBSCRIPTION;
   // Signed out because the only organization of somebody who can't manage its
   // billing was held while they were in it (Routes.js).
   const isLockedSignOut = searchParams.get("reason") === LogoutReason.ORGANIZATION_LOCKED;
   // Turned away here for the same reason, or because the owner deactivated
   // it: what the form says instead of signing in (title and description,
   // billingCodes.js describeOrganizationLock, describeDeactivatedOrganization).
   const [lockedSignIn, setLockedSignIn] = useState(null);

   const handleKeyPress = (e) => {
      if (e.key === "Enter") {
         e.preventDefault();
         formRef.current.submit();
      }
   };

   const handleCloseModal = () => {
      setIsModaOpen(false);
   };

   const confirmCode = () => {
      // console.log(formRef.current);
      form.submit();
   };

   const downloadImageAndSetSource = async (imageUrl) => {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
         const reader = new FileReader();

         reader.onloadend = async () => {
            const imageFile = new File([blob], "profile-picture.jpg", { type: blob.type });

            try {
               const scaledImage = await scaleImage(imageFile, 150);
               resolve(scaledImage);
            } catch (error) {
               console.error("Error scaling the downloaded image:", error);
               reject(error);
            }
         };

         reader.onerror = reject;
         reader.readAsDataURL(blob);
      });
   };

   const getProfilePicture = async (profilePictureUrl) => {
      const params = new URLSearchParams({
         platformId: Constants.PLATFORM_ID,
         fileKey: profilePictureUrl
      });

      httpService.get(
         `/FileData/getFileByFileKey?${params.toString()}`,
         async (response) => {
            try {
               const scaledImage = await downloadImageAndSetSource(response.data);

               const scaledReader = new FileReader();
               scaledReader.onloadend = () => {
                  const updatedUser = {
                     ...response.user,
                     profilePictureUrl: scaledReader.result
                  };
                  setPersonalData(updatedUser);
               };
               scaledReader.readAsDataURL(scaledImage);
            } catch (error) {
               console.error("Failed to load and scale profile picture:", error);
            }
         },
         (_err) => {
            console.log("Error while fetching profile picture:", _err);
         }
      );
   };

   const onFinish = async (values) => {
      if(!executeRecaptcha) {
         return;
      }

      // Signing in replaces whatever registration this tab was part way
      // through: everything below is written from what the login response
      // reports, and a key left over from a different account — an
      // organization id, a plan, a level — would be read alongside it as if it
      // belonged. Only the registration's own keys; the session is not just
      // ours. Merely opening this page takes nothing away, so a sign up that
      // came here to read something can go back to where it left off.
      clearRegistration();

      // The fields are marked wrong for the attempt that was wrong, not for
      // every one after it: a later failure of another kind is told on its own.
      setLoginError(false);
      setLockedSignIn(null);
      setIsLogin(true);

      const recaptchaToken = await executeRecaptcha('login');

      await httpService.post(
         "/account/login",
         {
            email: values.email,
            password: values.password
         },
         async (res) => {
            const resData = res.data;

            // The new billing flow signs this account in even though its only
            // organization never picked a plan. The plan is picked in the sign
            // up flow, so that is where it goes, with a token for the billing
            // calls that finish it — not as a sign in, which is what lets the
            // sign up pages open at all.
            if (
               resData.token &&
               hasBillingStatuses(resData.user?.organizations) &&
               isUnfinishedSignUp(resData.user.organizations)
            ) {
               // No plan yet to show a billing email's link for.
               takeBillingEmailLink();
               await resumeSignUp(resData);
               setIsLogin(false);
               return;
            }

            if (resData.token) {
               const organizations = resData.user.organizations;

               // A single organization is opened at once, so it is asked about
               // first, with the login's own token, before anything of the sign
               // in is kept: held at billing_only and not this user's to pay
               // for, it keeps them out, and the form says why. Somebody with
               // other organizations signs in and finds it locked in the list.
               let selected = null;
               if (organizations.length === 1) {
                  selected = await selectOnlyOrganization(resData);
                  if (!selected) {
                     notification.error({
                        message: "Oops! A problem has occurred!",
                        description: "We couldn't open your organization. Please try again."
                     });
                     setIsLogin(false);
                     return;
                  }

                  const lock = readOrganizationLock(selected.data, organizations[0].organizationId);
                  if (lock) {
                     setLockedSignIn(
                        describeOrganizationLock(lock.reason, organizations[0].organizationName)
                     );
                     setIsLogin(false);
                     return;
                  }
               }

               LocalStorageService.removeItem("organizationId");
               LocalStorageService.removeItem("organizationName");
               LocalStorageService.removeItem("roles");
               // Each organization's billing status, in place of any an earlier
               // sign in left behind. None at all with the new flow off.
               rememberSignInStatuses(resData);

               const updatedUser = {
                  ...resData.user,
                  profilePictureUrl: ""
               };
               setPersonalData(updatedUser);

               let profilePictureUrl = resData.user.profilePictureUrl;
               if (profilePictureUrl) {
                  getProfilePicture(profilePictureUrl);
               }
               LocalStorageService.setItem('atoken', resData?.token);

               if (organizations.length === 1) {
                  if (organizations[0].roles[0].name === "Organization") {
                     const differenceInDays = getLoginTrialReminderDays(organizations[0]);
                     if (differenceInDays !== null) {
                        LocalStorageService.setItem("addPaymentMethod", {
                           value: true,
                           differenceInDays: differenceInDays
                        });
                     }
               }
                  LocalStorageService.setItem("organizationId", organizations[0].organizationId);
                  LocalStorageService.setItem(
                     "organizationName",
                     organizations[0].organizationName
                  );
                  rememberSelectedStatus(selected.data, organizations[0].organizationId);
                  if (
                     selected.message?.includes(
                        `User for Organization ${organizations[0].organizationName} has been successfully reactivated!`
                     ) ||
                     selected.message?.includes("Organization has been successfully reactivated!")
                  ) {
                     LocalStorageService.setItem("reactivatedAccount", true);
                  }
                  LocalStorageService.setItem("tokenExpire", selected.data.tokenExpire);
                  LocalStorageService.setItem("roles", selected.data.userRole);
                  AuthService.setTokens(selected.data.token, selected.data.refreshToken);
               }
               // A billing email's link opened signed out goes on to its page,
               // where the routes select the organization it names.
               nav(takeBillingEmailLink() ?? "/");
            }
         },
         (error) => {
            // An email that has not been confirmed is confirmed from here
            // (login/loginFailures.js). The modal has a fresh code mailed as
            // it opens, to the account the refusal names.
            if (isEmailNotConfirmed(error)) {
               const account = (error?.response ? error.response.data : error)?.data?.user;
               setIsLogin(false);
               SessionService.setItem("currentUserId", account?.id);
               setUnconfirmedEmail(account?.email || values?.email);
               setIsModaOpen(true);
               return;
            }

            // A wrong email or password is told once, beside the fields. The
            // server's sentence for it says nothing the fields do not, and a
            // toast of it under them would report the one failure twice.
            if (isInvalidCredentials(error)) {
               setLoginError(true);
               setIsLogin(false);
               return;
            }

            const deactivated = readDeactivatedOrganization(error);
            if (deactivated) {
               setLockedSignIn(describeDeactivatedOrganization(deactivated.organizationName));
               setIsLogin(false);
               return;
            }

            // Anything else is said once, in the server's words when it sent
            // any — a refusal on a 200 carries them at the top, an HTTP error
            // on its response. Axios's own sentence comes last: it names the
            // status, never the reason.
            const errorMessage =
               error?.errors?.[0] ??
               error?.response?.data?.errors?.[0] ??
               error?.response?.data?.message ??
               error?.message;
            notification.error({
               message: "Oops! A problem has occurred!",
               description: errorMessage || "We couldn't sign you in. Please try again."
            });
            setIsLogin(false);
         },
         undefined,
         {
            'g-recaptcha-response': recaptchaToken
         }
      );
   };

   // selectOrganization for the account's only organization, sent with the
   // login's token since nothing of the sign in is stored yet. Resolves its
   // answer, or null when it gave none.
   const selectOnlyOrganization = async (resData) => {
      const organizationId = resData.user.organizations[0].organizationId;
      const params = new URLSearchParams({ userId: resData.user.id, organizationId });

      try {
         const response = await httpService.get(
            `/Account/selectOrganization?${params.toString()}`,
            undefined,
            undefined,
            {
               headers: {
                  Authorization: `Bearer ${resData.token}`,
                  "X-OrganizationId": organizationId
               }
            }
         );
         return response?.data?.token ? response : null;
      } catch {
         return null;
      }
   };

   // An account whose sign up stopped short of a plan, signed in by the new
   // billing flow. The sign up picks up at the package step — where the flow
   // before it sent the same account — now holding the token its billing calls
   // need (registrationSession.js). Filled in from the sign in the same way the
   // "No subscription" refusal below fills it in.
   const resumeSignUp = async (resData) => {
      const account = resData.user;
      const organization = account.organizations[0];

      // The login's own token only says who this is, and every billing call
      // refuses it. Without the organization's, the package step could not
      // start the trial it offers, so it is not opened.
      const opened = await openSignUpSession(resData.token, {
         userId: account.id,
         organizationId: organization.organizationId
      });
      if (!opened) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: "We couldn't open your account to finish signing up. Please try again."
         });
         return;
      }

      // Never subscribed, so no level has been recorded to narrow the plans to.
      SessionService.removeItem("registeredOrganizationLevel");
      SessionService.setItem("currentOrgId", organization.organizationId);
      SessionService.setItem("currentUserId", account.id);
      // A sign in at all means the address is proven.
      SessionService.setItem("isAccountConfirmed", true);
      SessionService.setItem("registeringAccount", {
         fullname: account.fullName,
         phone: account.phoneNumber,
         email: account.email,
         organizationName: organization.organizationName,
         typeOrganization: {
            name: organization.organizationType
         },
         country: { name: account.countryName }
      });
      nav("/auth/registration?step=package");
   };

   return (
      <>
         <div className="login-page-aside-logo">
            {/* start:: Aside header */}
            <a href="https://kapsurvey.com/" className="flex-column-auto mt-5 pt-20 pb-lg-0 pb-10">
               <img
                  alt="Logo"
                  className="max-h-40px logo-height"
                  src={toAbsoluteUrl("/media/logos/kapSurvey-logo.png")}
               />
            </a>
         </div>
         <div
            className="kap-survey-login"
            style={{
               position: "absolute",
               top: "50%",
               left: "50%",
               transform: "translate(-50%, -50%)",
               overflowX: "hidden"
            }}
         >
            <BlockUI isLoading={isLogin} title={""} />
            <ConfirmationCodeModal
               isModaOpen={isModaOpen}
               handleClose={handleCloseModal}
               confirmCode={confirmCode}
               email={unconfirmedEmail}
               sendCodeOnOpen
            />
            <Form
               form={form}
               name="basic"
               style={{
                  maxWidth: 600
               }}
               initialValues={{
                  remember: false
               }}
               onFinish={onFinish}
               onKeyPress={handleKeyPress}
               ref={formRef}
               autoComplete="off"
            >
               <div className="login-text">
                  <h1>Welcome Back</h1>
                  <h4>Sign in</h4>
               </div>

               {lockedSignIn ? (
                  <Alert
                     className="mb-6"
                     type="warning"
                     showIcon
                     message={lockedSignIn.title}
                     description={lockedSignIn.description}
                  />
               ) : isLockedSignOut ? (
                  <Alert
                     className="mb-6"
                     type="warning"
                     showIcon
                     message="Your organization's subscription has ended"
                     description="You were signed out because your organization's subscription has expired or was canceled. You'll have access again once its owner renews it."
                  />
               ) : (
                  isSubscriptionSignOut && (
                     <Alert
                        className="mb-6"
                        type="warning"
                        showIcon
                        message="Your subscription needs attention"
                        description="You were signed out because your organization's subscription has ended or its last payment didn't go through. Sign in again to see what's needed."
                     />
                  )
               )}

               <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                     {
                        required: true,
                        message: "We'll need your email to log you in. Go ahead and enter it."
                     }
                  ]}
                  validateStatus={loginError && "error"}
               >
                  <Input
                     placeholder="Type your email address..."
                     style={{
                        height: 40
                     }}
                  />
               </Form.Item>

               <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                     {
                        required: true,
                        message: "We'll need your password to log you in. Go ahead and enter it."
                     }
                  ]}
                  validateStatus={loginError && "error"}
               >
                  <Input.Password
                     placeholder="Type your password"
                     style={{
                        height: 40
                     }}
                  />
               </Form.Item>

               {loginError && <p className="errorMessage">Email or Password are incorrect!</p>}
               <Form.Item name="remember" valuePropName="checked">
                  <div className="d-flex justify-content-between">
                     <Checkbox>Remember me?</Checkbox>
                     <a href="/auth/forgot-password">Forgot your password?</a>
                  </div>
               </Form.Item>

               <Form.Item
                  style={{
                     height: 40
                  }}
               >
                  <Button
                     htmlType="submit"
                     className={`w-100 kapSurveyPrimaryButton`}
                     disabled={isLogin}
                  >
                     <Spin spinning={isLogin} size="small" style={{ marginRight: "8px" }} />
                     {isLogin ? "Processing..." : "Sign in"}
                  </Button>
               </Form.Item>
               <div className="text-center mt-5 font-size-h3">
                  Don’t have an account?{" "}
                  <Link
                     to="/auth/registration"
                     style={{ color: "#F58F45", textDecoration: "underline" }}
                  >
                     Sign up
                  </Link>
               </div>
            </Form>
         </div>
      </>
   );
}

export default injectIntl(Login);
